import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, ChevronRight, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Layout from '../components/layout/Layout';
import { newsEvents, fashionShowEvent, footballEvent, images, applicationFormUrl } from '../data/mock';

const News = () => {
  // Extended news items for the full page
  const additionalNews = [
    {
      id: 5,
      title: 'BIT Students Excel in National ICT Competition',
      date: 'June 25, 2025',
      excerpt: 'Students from the Science and Technology department secured top positions in the national ICT skills competition.',
      image: images.techLab,
      category: 'Achievement',
    },
    {
      id: 6,
      title: 'Industry Mentorship Programme Launched',
      date: 'June 10, 2025',
      excerpt: 'BIT partners with leading companies to provide mentorship opportunities for Business department students.',
      image: images.business,
      category: 'Programme',
    },
    {
      id: 7,
      title: 'Open Day Event - Meet Our Faculty',
      date: 'May 28, 2025',
      excerpt: 'Prospective students and parents are invited to visit our campus and learn about our programmes.',
      image: images.students,
      category: 'Event',
    },
  ];

  const allNews = [...newsEvents, ...additionalNews];

  const upcomingEvents = [
    {
      title: 'September Intake Orientation',
      date: 'September 2, 2025',
      description: 'Welcome ceremony for new students joining the September intake.',
    },
    {
      title: 'Career Fair 2025',
      date: 'September 15, 2025',
      description: 'Connect with potential employers from various industries.',
    },
    {
      title: 'Research Symposium',
      date: 'October 5, 2025',
      description: 'Annual presentation of student and faculty research projects.',
    },
  ];

  const getEventLink = (item) => {
    if (item.id === 'bit-fashion-show-and-modeling-event') {
      return '/news/bit-fashion-show-and-modeling-event';
    }
    if (item.id === 'bit-football-match-campus-sports-event') {
      return '/news/bit-football-match-campus-sports-event';
    }
    return '#';
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${images.campus})` }}
        >
          <div className="absolute inset-0 overlay-navy" />
        </div>
        <div className="container-custom relative z-10">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
            News & Events
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Stay updated with the latest happenings, achievements, and events at BIT.
          </p>
        </div>
      </section>

      {/* Featured Event - Football Match */}
      <section className="section-padding bg-[#f8fafc]">
        <div className="container-custom">
          <h2 className="font-display text-2xl font-bold text-[#1e3a5f] mb-6">
            Featured Event
          </h2>
          <Card className="overflow-hidden border-none shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="relative h-64 lg:h-auto">
                <img
                  src={footballEvent.images[0]}
                  alt={footballEvent.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#1e3a5f]/30" />
              </div>
              <CardContent className="p-8">
                <Badge className="bg-[#d4a855] text-[#142942] mb-4">
                  {footballEvent.category}
                </Badge>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-[#1e3a5f] mb-4">
                  {footballEvent.title}
                </h3>
                <div className="flex flex-wrap gap-4 text-[#475569] text-sm mb-4">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4 text-[#d4a855]" />
                    {footballEvent.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-[#d4a855]" />
                    {footballEvent.location}
                  </span>
                </div>
                <p className="text-[#475569] mb-6 line-clamp-3">
                  {footballEvent.description}
                </p>
                <Link to="/news/bit-football-match-campus-sports-event">
                  <Button className="bg-[#1e3a5f] hover:bg-[#2563eb] text-white">
                    Read More
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </div>
          </Card>
        </div>
      </section>

      {/* News Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Main News */}
            <div className="lg:col-span-2">
              <h2 className="font-display text-2xl font-bold text-[#1e3a5f] mb-6">
                Latest News
              </h2>
              <div className="space-y-6">
                {allNews.slice(1).map((item) => (
                  <Card key={item.id} className="overflow-hidden border-none shadow-md card-hover">
                    <div className="grid grid-cols-1 md:grid-cols-3">
                      <div className="relative h-48 md:h-auto">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-[#1e3a5f]/20" />
                      </div>
                      <CardContent className="md:col-span-2 p-6">
                        <div className="flex items-center gap-3 mb-3">
                          {item.category && (
                            <Badge className="bg-[#1e3a5f] text-white text-xs">
                              {item.category}
                            </Badge>
                          )}
                          <span className="text-[#d4a855] text-sm font-medium flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {item.date}
                          </span>
                        </div>
                        <h3 className="font-display font-semibold text-[#1e3a5f] text-xl mb-2">
                          {item.title}
                        </h3>
                        <p className="text-[#475569] text-sm line-clamp-2 mb-4">
                          {item.excerpt}
                        </p>
                        <Link to={getEventLink(item)}>
                          <button className="text-[#2563eb] font-medium text-sm flex items-center group">
                            Read More
                            <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                          </button>
                        </Link>
                      </CardContent>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Upcoming Events */}
              <div className="sticky top-24">
                <h2 className="font-display text-2xl font-bold text-[#1e3a5f] mb-6">
                  Upcoming Events
                </h2>
                <div className="space-y-4">
                  {upcomingEvents.map((event, index) => (
                    <Card key={index} className="border-l-4 border-[#d4a855] shadow-sm">
                      <CardContent className="py-4">
                        <span className="text-[#d4a855] text-sm font-medium flex items-center gap-1 mb-2">
                          <Calendar className="w-4 h-4" />
                          {event.date}
                        </span>
                        <h4 className="font-semibold text-[#1e3a5f] mb-1">
                          {event.title}
                        </h4>
                        <p className="text-[#475569] text-sm">
                          {event.description}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Newsletter */}
                <Card className="mt-8 bg-[#1e3a5f] border-none">
                  <CardContent className="py-6">
                    <h4 className="font-display font-semibold text-white text-lg mb-2">
                      Stay Informed
                    </h4>
                    <p className="text-white/70 text-sm mb-4">
                      Subscribe to our newsletter for updates on admissions, events, and more.
                    </p>
                    <input
                      type="email"
                      placeholder="Enter your email"
                      className="w-full px-4 py-2 rounded-lg mb-3 text-sm"
                    />
                    <Button className="w-full bg-[#d4a855] hover:bg-[#f4d794] text-[#142942]">
                      Subscribe
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#f8fafc]">
        <div className="container-custom text-center">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[#1e3a5f] mb-4">
            Want to Be Part of Our Story?
          </h2>
          <p className="text-[#475569] max-w-xl mx-auto mb-8">
            Join BIT and become part of our growing community of future leaders.
          </p>
          <a href={applicationFormUrl} target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-[#1e3a5f] hover:bg-[#2563eb] text-white font-semibold px-8">
              Apply Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default News;
